import React, { useState } from "react";

import { Link } from "react-router-dom";

import { buildClassName } from "shared/utils/vega";

import StatusIcon, { StatusType, getStatusColor } from "../../StatusIcon";

import ApplicantDetailModal, { ModalSection } from "./ApplicantDetailModal";

export interface CardDetailItem {
  label?: string;
  value: string;
  status?: string;
  id?: string;
}

export interface ApplicantGroupCardProps {
  cardTitle: string;
  status: StatusType;
  cardDetails: CardDetailItem[];
  cardModalDetails?: ModalSection[];
  allDetailsLink?: string;
}

export type ApplicantCardDetailsProps = ApplicantGroupCardProps;

/**
 * Component that displays a card with status icon and details
 */
const ApplicantGroupCard: React.FC<ApplicantGroupCardProps> = ({
  cardTitle,
  status,
  cardDetails,
  cardModalDetails,
  allDetailsLink,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleLinkClick = (e) => {
    e.stopPropagation(); // Prevent the card's onClick from triggering
  };

  return (
    <>
      <button
        className={buildClassName([
          "app-summary-v2 vega flex gap small column card service",
          "status-bar",
          status,
        ])}
        onClick={cardModalDetails ? openModal : undefined}
        type="button"
        aria-label={`View details for ${cardTitle}`}
        style={{
          cursor: cardModalDetails ? "pointer" : "default",
          background: "none",
          textAlign: "left",
          padding: 0,
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          border: "1px solid rgba(0, 0, 0, 0.06)",
          borderRadius: "8px",
          transition: "all 0.2s ease-in-out",
        }}
        onMouseEnter={(e) => {
          if (cardModalDetails) {
            e.currentTarget.style.boxShadow =
              "0 4px 16px rgba(0, 0, 0, 0.15), 0 0 0 3px rgba(79, 70, 229, 0.3)";
            e.currentTarget.style.transform = "translateY(-2px) scale(1.02)";
          }
        }}
        onMouseLeave={(e) => {
          if (cardModalDetails) {
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
            e.currentTarget.style.transform = "translateY(0) scale(1)";
          }
        }}
      >
        <div
          className="vega flex gap small column"
          style={{ padding: "12px", flex: "1 1 auto" }}
        >
          <div className="vega flex start center gap small">
            <StatusIcon status={status} />
            <span className="vega title">{cardTitle}</span>
          </div>
          <div className="vega flex gap small column">
            {cardDetails.map((detail) => (
              <div
                key={
                  detail.id || `detail-label-${detail.label}-${detail.value}`
                }
                className="vega flex column"
              >
                <span className="vega text">
                  {detail.label && `${detail.label}: `}
                  <span
                    style={
                      detail.status
                        ? { color: getStatusColor(detail.status) }
                        : undefined
                    }
                  >
                    {detail.value}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>

        {allDetailsLink && (
          <div
            className="app-summary-v2 card-footer"
            style={{ flexShrink: 0, marginTop: "auto" }}
          >
            <Link
              to={allDetailsLink}
              className="app-summary-v2 vega details-link"
              onClick={handleLinkClick}
            >
              View all details
            </Link>
          </div>
        )}
      </button>

      {cardModalDetails && (
        <ApplicantDetailModal
          isOpen={isModalOpen}
          onClose={closeModal}
          cardTitle={cardTitle}
          cardDetails={cardDetails}
          cardModalDetails={cardModalDetails}
          status={status}
        />
      )}
    </>
  );
};

export default ApplicantGroupCard;
